import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Percent, FileCheck2, ArrowRight } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ProgramConfig } from '../../../types/aai';
import { useParticipant } from '../../../hooks/useParticipant';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, PageHeader, Toggle } from '../dashboards/ui';

const PRESETS = [
  { value: 'coe_full', label: 'Full CoE', detail: 'Educate / Empower / Equip · monthly check-ins' },
  { value: 'coe_lite', label: 'CoE-lite', detail: 'Clarity + registry + quick-wins' },
  { value: 'custom', label: 'Custom', detail: 'Define your own modules' },
];

const TIERS = [
  { value: '3mo', label: '3-month', detail: 'Modules 1–4 — capable operator with a vetted personal skill pack' },
  { value: '6mo', label: '6-month', detail: 'Modules 1–6 — team adoption + AA certification' },
  { value: '12mo', label: '12-month', detail: 'Modules 1–6 + quarterly loops (coming soon)' },
];

// The standing privacy rules behind the program — described qualitatively (this
// is how the platform behaves), not as editable numbers the page can't persist.
const GOVERNANCE = [
  { icon: Lock, name: 'Min group size (privacy)', detail: 'Groups below the minimum are suppressed, not shown.' },
  { icon: Percent, name: 'Self-report discount', detail: 'Self-reported gains are discounted before any rollup.' },
  { icon: FileCheck2, name: 'Individual drill-in', detail: 'Champion-only, and every view writes an audit-log entry.' },
];

const AaiProgramPage: React.FC = () => {
  const navigate = useNavigate();
  const { context } = useParticipant();
  // The Owner is a read-only sponsor (PRD F1): they can enable/disable the
  // program but not restructure it (preset / phases). Program-managers
  // (champion / consultant / trainer) can.
  const canManage = !!context?.capabilities?.manage_program;
  const [cfg, setCfg] = useState<ProgramConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    aaiApi.getProgram().then((r) => setCfg(r.data)).catch(() => setError('Could not load the program.')).finally(() => setLoading(false));
  }, []);

  const save = async (patch: Partial<ProgramConfig>) => {
    setBusy(true);
    try { const { data } = await aaiApi.updateProgram(patch); setCfg(data); }
    catch { setError('Could not save.'); }
    finally { setBusy(false); }
  };

  if (loading) return <Loading label="Loading the program…" />;
  if (error || !cfg) return <ErrorNote message={error || 'Something went wrong.'} />;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="AAI program"
        subtitle="The configuration behind the whole adoption program — methodology, privacy thresholds, and access rules. Turning this off hides every AI Adoption nav item for all roles."
      />

      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div><div className="text-[14px] font-semibold">{cfg.name}</div><div className="text-[12.5px] text-gray-500">{cfg.enabled ? 'Enabled for this workspace' : 'Disabled'}</div></div>
          <Toggle on={cfg.enabled} disabled={busy} onChange={() => save({ enabled: !cfg.enabled })} />
        </div>
      </Card>

      <Card className="mb-4">
        <MonoLabel className="mb-3">Methodology preset</MonoLabel>
        <div className="space-y-2">
          {PRESETS.map((p) => (
            <button key={p.value} disabled={busy || !canManage}
              onClick={() => {
                if (!canManage) return;
                if (p.value !== 'custom' && p.value !== cfg.preset &&
                    !window.confirm(`Switching to ${p.label} applies that template — it replaces this workspace's modules & lessons. Continue?`)) return;
                save({ preset: p.value });
              }}
              className={`w-full text-left bg-white rounded-lg px-3.5 py-3 border transition-colors ${cfg.preset === p.value ? 'border-brand-400 ring-1 ring-brand-200' : 'border-gray-100'} ${canManage ? 'hover:border-gray-200' : 'cursor-default opacity-90'}`}>
              <div className="flex items-center justify-between"><span className="font-semibold text-[13.5px]">{p.label}</span>{cfg.preset === p.value && <span className="text-[11px] font-semibold text-brand-600">Active</span>}</div>
              <div className="text-[12px] text-gray-500 mt-0.5">{p.detail}</div>
            </button>
          ))}
        </div>
        {!canManage && <p className="text-[11.5px] text-gray-400 mt-3">Your champion or trainer sets the methodology — this is a read-only view.</p>}
      </Card>

      <Card className="mb-4">
        <MonoLabel className="mb-3">Program tier</MonoLabel>
        <div className="space-y-2">
          {TIERS.map((t) => {
            const active = (cfg.tier || '6mo') === t.value;
            return (
              <button key={t.value} disabled={busy || !canManage}
                onClick={() => { if (canManage) void save({ tier: t.value }); }}
                className={`w-full text-left bg-white rounded-lg px-3.5 py-3 border transition-colors ${active ? 'border-brand-400 ring-1 ring-brand-200' : 'border-gray-100'} ${canManage ? 'hover:border-gray-200' : 'cursor-default opacity-90'}`}>
                <div className="flex items-center justify-between"><span className="font-semibold text-[13.5px]">{t.label}</span>{active && <span className="text-[11px] font-semibold text-brand-600">Active</span>}</div>
                <div className="text-[12px] text-gray-500 mt-0.5">{t.detail}</div>
              </button>
            );
          })}
        </div>
        {canManage ? (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <label className="text-[12.5px] text-gray-600 font-medium">Start date</label>
            <input type="date" value={cfg.starts_on || ''} disabled={busy}
              onChange={(e) => void save({ starts_on: e.target.value })}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
            <span className="text-[11px] text-gray-400">drives the module calendar</span>
          </div>
        ) : (
          <p className="text-[11.5px] text-gray-400 mt-3">The tier is set by your champion or trainer.</p>
        )}
      </Card>

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <MonoLabel>Modules & lessons</MonoLabel>
          {canManage && (
            <button onClick={() => navigate('/aai/curriculum')}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:text-brand-700">
              Edit modules & lessons <ArrowRight size={13} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {cfg.phases.map((ph) => <span key={ph.key} className="rounded-full bg-white border border-gray-200 px-3 py-1 text-[12.5px] font-medium text-gray-700">{ph.label}</span>)}
        </div>
        <p className="text-[11.5px] text-gray-400 mt-3">Modules are pluggable — nothing downstream hardcodes them; dashboards read whatever a program defines. Edit the modules and their lessons in the curriculum editor.</p>
      </Card>

      <Card>
        <MonoLabel className="mb-3">Governance & privacy</MonoLabel>
        <div className="space-y-2">
          {GOVERNANCE.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.name} className="flex items-start gap-2.5 bg-white rounded-lg px-3.5 py-2.5">
                <Icon size={15} className="text-brand-500 mt-0.5 shrink-0" />
                <div><div className="text-[13px] font-semibold">{g.name}</div><div className="text-[12px] text-gray-500">{g.detail}</div></div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default AaiProgramPage;
